import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { EmptyStateComponent } from '../../../ui/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';
import { ReviewDecisionComponent } from './review-decision/review-decision.component';
import { ReviewHistoryComponent } from './review-history/review-history.component';
import { QuestionSourceComponent } from '../question-source/question-source.component';

import { AnswerBank } from '../../../../core/interfaces/answer-bank';
import {
  BulkReviewResult,
  QuestionReview,
} from '../../../../core/interfaces/question-review';
import { QuestionBankItem } from '../../../../core/interfaces/question-search';
import { QuestionReviewService } from '../../../../core/services/questions/question-review.service';
import { QuestionSearchService } from '../../../../core/services/questions/question-search.service';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { MathTextComponent } from '../../../../shared/math/math-text.component';

/* ══════════════════════════════════════════════════════════════
   VOCABULAIRE → CLÉS I18N

   Des tables explicites, pas une clé construite par concaténation :
   une valeur inconnue renvoyée par le back doit être affichée telle
   quelle, pas transformée en clé de traduction manquante.
   ══════════════════════════════════════════════════════════════ */

const TYPE_KEYS: Record<string, string> = {
  single_choice: 'REVIEW_QUEUE.TYPE_SINGLE_CHOICE',
  multiple_choice: 'REVIEW_QUEUE.TYPE_MULTIPLE_CHOICE',
  true_false: 'REVIEW_QUEUE.TYPE_TRUE_FALSE',
  short_text: 'REVIEW_QUEUE.TYPE_SHORT_TEXT',
  numeric: 'REVIEW_QUEUE.TYPE_NUMERIC',
  matching: 'REVIEW_QUEUE.TYPE_MATCHING',
  ordering: 'REVIEW_QUEUE.TYPE_ORDERING',
  cloze: 'REVIEW_QUEUE.TYPE_CLOZE',
};

const DIFFICULTY_KEYS: Record<string, string> = {
  easy: 'REVIEW_QUEUE.DIFFICULTY_EASY',
  medium: 'REVIEW_QUEUE.DIFFICULTY_MEDIUM',
  hard: 'REVIEW_QUEUE.DIFFICULTY_HARD',
};

const ORIGIN_KEYS: Record<string, string> = {
  manual: 'REVIEW_QUEUE.ORIGIN_MANUAL',
  imported: 'REVIEW_QUEUE.ORIGIN_IMPORTED',
  ai_generated: 'REVIEW_QUEUE.ORIGIN_AI_GENERATED',
};

/** Ligne de la file, préparée pour l'affichage. */
interface QueueRow {
  item: QuestionBankItem;
  /** Ancienneté en jours pleins, `null` si le back n'a pas daté la question. */
  ageDays: number | null;
  /** Une question produite par une machine ne se relit pas comme une autre. */
  isAiGenerated: boolean;
}

const MILLISECONDS_PER_DAY = 86_400_000;

/**
 * File de relecture des questions.
 *
 * Écran de traitement de volume : la file à gauche, la question rendue telle
 * que l'élève la verra à droite, la décision et l'historique dessous. Après
 * chaque décision l'item quitte la file et le suivant est sélectionné, sans
 * rechargement complet.
 *
 * Aucune donnée n'est fabriquée : une erreur HTTP donne `app-error-state`, une
 * file vide donne `app-empty-state` — ici une bonne nouvelle, il n'y a rien à
 * relire.
 */
@Component({
  selector: 'app-review-queue',
  standalone: true,
  imports: [MathTextComponent, 
    RouterLink,
    QuestionSourceComponent,
    TranslateModule,
    EmptyStateComponent,
    ErrorStateComponent,
    ReviewDecisionComponent,
    ReviewHistoryComponent,
  ],
  templateUrl: './review-queue.component.html',
  styleUrl: './review-queue.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewQueueComponent implements OnInit {
  private readonly searchService = inject(QuestionSearchService);
  private readonly reviewService = inject(QuestionReviewService);
  private readonly swal = inject(SwalService);
  private readonly translate = inject(TranslateService);

  /**
   * Page large : l'écran se parcourt au clavier d'un bout à l'autre, et le
   * back plafonne de toute façon à 200.
   */
  private static readonly PAGE_SIZE = 50;

  /* ── File ──────────────────────────────────────────────── */

  private readonly items = signal<QuestionBankItem[]>([]);
  readonly loading = signal(false);
  /** Détail technique de l'échec de chargement, `null` si tout va bien. */
  readonly loadError = signal<string | null>(null);
  /** Total renvoyé par le back, toutes pages confondues. */
  readonly total = signal(0);

  readonly rows = computed<QueueRow[]>(() =>
    this.items().map((item) => ({
      item,
      ageDays: ageInDays(item),
      isAiGenerated: item.origin === 'ai_generated',
    })),
  );

  /* ── Sélection ─────────────────────────────────────────── */

  readonly selectedId = signal<number | null>(null);

  readonly selected = computed<QuestionBankItem | null>(() => {
    const id = this.selectedId();
    return this.items().find((item) => item.id === id) ?? null;
  });

  /** Vrai pendant l'aller-retour d'une décision : verrouille le panneau. */
  readonly deciding = signal(false);

  /* ── Réponses de la question sélectionnée ──────────────── */

  private readonly options = signal<AnswerBank[]>([]);
  /**
   * Vrai quand les réponses n'ont pas pu être obtenues. On l'affiche
   * explicitement plutôt que de rendre un aperçu sans options qui laisserait
   * croire que la question n'en a pas.
   */
  readonly optionsUnavailable = signal(false);

  /**
   * Les descripteurs que l'import a portés, dans l'ordre des colonnes du modèle.
   *
   * <p><b>Pourquoi les montrer ici.</b> La relecture décide si une question
   * entre au référentiel. Elle se faisait sur l'énoncé, les options et le
   * thème — alors que ce qui <i>range</i> la question, c'est la compétence, le
   * niveau et les prérequis. Valider sans les voir, c'est approuver la moitié
   * de ce qu'on approuve.</p>
   *
   * <p>Les vides sont écartés : quatre lignes « — » noieraient les deux qui
   * ont quelque chose à dire.</p>
   */
  readonly descripteurs = computed<{ labelKey: string; value: string }[]>(() => {
    const m = this.selected()?.matchingOptions;
    if (!m) return [];

    const candidats: readonly (readonly [string, string | null | undefined])[] = [
      ['TEST_IMPORT.ROLE.SKILL', m.skill],
      ['TEST_IMPORT.ROLE.TARGETED', m.targeted],
      ['TEST_IMPORT.ROLE.LEVEL', m.level],
      ['TEST_IMPORT.ROLE.PREREQUISITES', m.prerequisites],
    ];

    return candidats
      .filter(([, valeur]) => !!valeur?.trim())
      .map(([labelKey, valeur]) => ({ labelKey, value: valeur!.trim() }));
  });

  /**
   * Le retour vers la file, passé à l'écran d'édition.
   *
   * <p>La relecture n'édite pas elle-même : elle confie à l'éditeur existant,
   * qui sait déjà tout faire — options, compétences, provenance — et qui
   * ramène ici une fois enregistré. Un second éditeur aurait divergé du
   * premier au premier champ ajouté.</p>
   */
  readonly retourVersLaFile = { returnUrl: '/admin/review-queue' };

  readonly sortedOptions = computed<AnswerBank[]>(() =>
    [...this.options()].sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
    ),
  );

  /* ── Historique ────────────────────────────────────────── */

  readonly history = signal<QuestionReview[]>([]);
  readonly historyLoading = signal(false);
  readonly historyErrored = signal(false);

  ngOnInit(): void {
    this.load();
  }

  /* ── Chargement de la file ─────────────────────────────── */

  load(): void {
    this.loading.set(true);
    this.loadError.set(null);

    this.searchService
      .reviewQueue({ page: 1, pageSize: ReviewQueueComponent.PAGE_SIZE })
      .subscribe({
        next: (response) => {
          const list = response?.data ?? [];
          this.items.set(list);
          this.total.set(response?.pagination?.total ?? list.length);
          this.loading.set(false);

          const stillThere = list.some((item) => item.id === this.selectedId());
          if (!stillThere) {
            this.selectAt(0);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.items.set([]);
          this.total.set(0);
          this.applySelection(null);
          this.loadError.set(errorDetail(error));
          this.loading.set(false);
        },
      });
  }

  /* ── Sélection ─────────────────────────────────────────── */

  select(item: QuestionBankItem): void {
    if (item.id === this.selectedId()) {
      return;
    }
    this.applySelection(item);
  }

  private selectAt(index: number): void {
    const list = this.items();
    const item = index >= 0 && index < list.length ? list[index] : null;
    this.applySelection(item);
  }

  private applySelection(item: QuestionBankItem | null): void {
    this.selectedId.set(item?.id ?? null);

    this.options.set([]);
    this.optionsUnavailable.set(false);
    this.history.set([]);
    this.historyLoading.set(false);
    this.historyErrored.set(false);

    if (!item) {
      return;
    }
    this.loadOptions(item);
    this.loadHistory(item.id);
  }

  /**
   * Déplace la sélection dans la file (raccourcis `J` / `K` et flèches).
   * Bornée aux extrémités : on ne boucle pas, sinon on relit deux fois la même
   * question sans s'en rendre compte.
   */
  private moveSelection(offset: number): void {
    const list = this.items();
    if (list.length === 0) {
      return;
    }
    const current = list.findIndex((item) => item.id === this.selectedId());
    const target =
      current < 0
        ? 0
        : Math.min(list.length - 1, Math.max(0, current + offset));

    if (target === current) {
      return;
    }
    this.selectAt(target);
    this.revealSelected();
  }

  /** Garde la ligne sélectionnée visible quand on navigue au clavier. */
  private revealSelected(): void {
    const id = this.selectedId();
    if (id === null) {
      return;
    }
    queueMicrotask(() => {
      document
        .getElementById(`rq-item-${id}`)
        ?.scrollIntoView({ block: 'nearest' });
    });
  }

  /* ── Réponses ──────────────────────────────────────────── */

  /**
   * Les réponses arrivent avec la file : l'endpoint `/review-queue` force leur
   * chargement (`IncludeAnswers`), précisément parce qu'on ne peut pas valider
   * une question sans voir ses options.
   *
   * Une version antérieure refaisait une recherche par thème filtrée sur un
   * extrait de l'énoncé pour les récupérer — contournement fragile, remplacé
   * par le correctif côté serveur. Si les options manquent malgré tout, on
   * l'annonce plutôt que d'en inventer.
   */
  private loadOptions(item: QuestionBankItem): void {
    const answers = item.answerBanks ?? [];
    this.options.set(answers);
    this.optionsUnavailable.set(answers.length === 0);
  }

  /* ── Historique ────────────────────────────────────────── */

  reloadHistory(): void {
    const id = this.selectedId();
    if (id !== null) {
      this.loadHistory(id);
    }
  }

  private loadHistory(questionId: number): void {
    this.historyLoading.set(true);
    this.historyErrored.set(false);

    this.reviewService.history(questionId).subscribe({
      next: (response) => {
        if (this.selectedId() !== questionId) {
          return;
        }
        this.history.set(response?.data ?? []);
        this.historyLoading.set(false);
      },
      error: () => {
        if (this.selectedId() !== questionId) {
          return;
        }
        this.history.set([]);
        this.historyErrored.set(true);
        this.historyLoading.set(false);
      },
    });
  }

  /* ── Décisions ─────────────────────────────────────────── */

  onApprove(comment?: string): void {
    const item = this.selected();
    if (!item || this.deciding()) {
      return;
    }
    this.deciding.set(true);
    this.reviewService.approve(item.id, comment).subscribe({
      next: () => this.afterDecision(item.id, 'REVIEW_QUEUE.APPROVED_TOAST'),
      error: (error: HttpErrorResponse) => this.onDecisionError(error),
    });
  }

  onReject(comment: string): void {
    const item = this.selected();
    if (!item || this.deciding()) {
      return;
    }
    this.deciding.set(true);
    this.reviewService.reject(item.id, comment).subscribe({
      next: () => this.afterDecision(item.id, 'REVIEW_QUEUE.REJECTED_TOAST'),
      error: (error: HttpErrorResponse) => this.onDecisionError(error),
    });
  }

  /**
   * Valide toute la file affichée en une seule requête.
   *
   * Le lot part d'un coup : enchaîner un appel par question déclencherait
   * l'anti-flood du back — cent requêtes par minute, puis bannissement de
   * l'IP — et le relecteur serait bloqué pour avoir fait son travail.
   *
   * L'action porte sur les questions <b>chargées</b>. Si la file en compte
   * plus que la page n'en montre, la confirmation le dit : annoncer « tout »
   * en n'en traitant qu'une partie laisserait le reste en attente sans que
   * personne le voie.
   */
  async onApproveAll(): Promise<void> {
    const ids = this.items().map((item) => item.id);
    if (ids.length === 0 || this.deciding()) {
      return;
    }

    const hidden = Math.max(0, this.total() - ids.length);
    const confirmed = await this.swal.confirmAsync(
      this.translate.instant(
        hidden > 0
          ? 'REVIEW_QUEUE.APPROVE_ALL_CONFIRM_PARTIAL'
          : 'REVIEW_QUEUE.APPROVE_ALL_CONFIRM',
        { count: ids.length, hidden },
      ),
    );
    if (!confirmed) {
      return;
    }

    this.deciding.set(true);
    this.reviewService.approveBatch(ids).subscribe({
      next: (response) => this.afterBulkDecision(response?.data),
      error: (error: HttpErrorResponse) => this.onDecisionError(error),
    });
  }

  /**
   * Rend compte du lot.
   *
   * Les questions écartées — déjà traitées ailleurs, ou disparues — sont
   * annoncées plutôt que passées sous silence : le rechargement les remettrait
   * dans la file sans explication.
   */
  private afterBulkDecision(result?: BulkReviewResult | null): void {
    this.deciding.set(false);

    if (!result) {
      this.load();
      return;
    }

    const message =
      result.failed > 0
        ? this.translate.instant('REVIEW_QUEUE.APPROVE_ALL_PARTIAL', {
            count: result.succeeded,
            failed: result.failed,
          })
        : this.translate.instant('REVIEW_QUEUE.APPROVE_ALL_DONE', {
            count: result.succeeded,
          });

    if (result.failed > 0) {
      this.swal.error(message);
    } else {
      this.swal.success(message);
    }

    // Rechargement plutôt que retrait local : après un lot, ce qui reste en
    // attente ne se déduit pas de ce qu'on affichait.
    this.load();
  }

  /**
   * Retire la question traitée et enchaîne sur la suivante : c'est la place
   * libérée qui devient la sélection, donc la question qui suivait celle qu'on
   * vient de trancher. En fin de file, on remonte sur la dernière restante.
   */
  private afterDecision(questionId: number, messageKey: string): void {
    this.deciding.set(false);

    const list = this.items();
    const index = list.findIndex((item) => item.id === questionId);
    const remaining = list.filter((item) => item.id !== questionId);

    this.items.set(remaining);
    this.total.update((value) => (value > 0 ? value - 1 : 0));
    this.swal.success(this.translate.instant(messageKey));

    this.selectAt(Math.min(index < 0 ? 0 : index, remaining.length - 1));
    this.revealSelected();
  }

  private onDecisionError(error: HttpErrorResponse): void {
    this.deciding.set(false);
    const detail = errorDetail(error);
    this.swal.error(
      detail || this.translate.instant('REVIEW_QUEUE.DECISION_FAILED'),
    );
  }

  /* ── Raccourcis de navigation ──────────────────────────── */

  /**
   * `J` / flèche bas : question suivante. `K` / flèche haut : précédente.
   *
   * Les raccourcis de décision (`A`, `R`) sont portés par le panneau de
   * décision, seul détenteur du motif et de sa validité. Ici comme là-bas, un
   * focus dans un champ de saisie neutralise l'écoute.
   */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (isEditableTarget(event.target)) {
      return;
    }
    if (this.loading() || this.rows().length === 0) {
      return;
    }

    const key = event.key.toLowerCase();
    if (key === 'j' || key === 'arrowdown') {
      event.preventDefault();
      this.moveSelection(1);
    } else if (key === 'k' || key === 'arrowup') {
      event.preventDefault();
      this.moveSelection(-1);
    }
  }

  /* ── Libellés ──────────────────────────────────────────── */

  /** Clé i18n du type, ou `null` si le back renvoie une valeur inconnue. */
  typeKey(value: string): string | null {
    return TYPE_KEYS[value] ?? null;
  }

  difficultyKey(value: string): string | null {
    return DIFFICULTY_KEYS[value] ?? null;
  }

  originKey(value: string): string | null {
    return ORIGIN_KEYS[value] ?? null;
  }

  /** Lettre affichée devant une option (A, B, C…), comme côté élève. */
  optionLetter(index: number): string {
    return String.fromCharCode(65 + index);
  }
}

/* ══════════════════════════════════════════════════════════════
   FONCTIONS LIBRES
   ══════════════════════════════════════════════════════════════ */

/**
 * Ancienneté en jours pleins. `null` quand la date est absente ou illisible :
 * une question non datée n'a pas d'âge, elle n'a pas « zéro jour ».
 */
function ageInDays(item: QuestionBankItem): number | null {
  const raw = item.createdAt;
  if (!raw) {
    return null;
  }
  const created = new Date(raw as unknown as string).getTime();
  if (!Number.isFinite(created)) {
    return null;
  }
  return Math.max(0, Math.floor((Date.now() - created) / MILLISECONDS_PER_DAY));
}

/**
 * Vrai quand l'événement provient d'une zone de saisie. Dupliqué dans le
 * panneau de décision : les deux composants écoutent `document` séparément.
 */
function isEditableTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element || !element.tagName) {
    return false;
  }
  const tag = element.tagName.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    element.isContentEditable === true
  );
}

/** Message d'erreur lisible tiré de la réponse HTTP, sans rien inventer. */
function errorDetail(error: HttpErrorResponse): string {
  const body = error?.error as { message?: string } | string | null | undefined;

  if (typeof body === 'string' && body.trim().length > 0) {
    return body.trim();
  }
  if (
    body &&
    typeof body === 'object' &&
    typeof body.message === 'string' &&
    body.message.trim().length > 0
  ) {
    return body.message.trim();
  }
  return `${error?.status ?? ''} ${error?.statusText ?? ''}`.trim();
}
